import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import HomePage from './components/HomePage';
import BlogHome from './pages/blog/BlogHome';
import BlogModalidades from './pages/blog/BlogModalidades';
import BlogMultijugador from './pages/blog/BlogMultijugador';
import BlogMesa from './pages/blog/BlogMesa';
import CreateRoom from './components/CreateRoom';
import JoinRoom from './components/JoinRoom';
import GameRoom from './components/GameRoom';
import Contact from './components/Contact';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import AboutUs from './components/AboutUs';
import TableSetup from './components/table/TableSetup';
import TableGame from './components/table/TableGame';
import NotFound from './components/NotFound';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import Header from './components/layout/Header';
import TopBannerAd from './components/layout/TopBannerAd';
import { getStructuredData, getWebSiteStructuredData, getBreadcrumbStructuredData, getOrganizationStructuredData } from './utils/structuredData';
import LocaleSync from './i18n/LocaleSync';
import { getLocaleFromPathname, stripLocalePrefix } from './i18n/routing';
import './App.css';

// Componente que decide si mostrar el footer basado en la ruta
const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();

  // Normalizar pathname para que /en/... use la misma lógica que /...
  const normalizedPathname = stripLocalePrefix(location.pathname);
  
  // Lista de rutas válidas conocidas
  const validRoutes = [
    '/',
    '/create',
    '/join',
    '/table',
    '/blog',
    '/blog/modalidad',
    '/blog/modalidad/multijugador',
    '/blog/modalidad/mesa',
    '/contact',
    '/privacy-policy',
    '/terms-of-service',
    '/about'
  ];
  
  // Verificar si es una ruta dinámica válida o una ruta estática válida
  const isValidRoute = validRoutes.includes(normalizedPathname) ||
                       normalizedPathname.startsWith('/room/') ||
                       (normalizedPathname.startsWith('/table/') && normalizedPathname.endsWith('/play'));
  
  // Si no es una ruta válida, es una página 404
  const is404Page = !isValidRoute;
  
  // Rutas que ya tienen su propio Footer (usan BaseLayout)
  const hasOwnFooter = normalizedPathname.startsWith('/blog') || 
                       ['/contact', '/privacy-policy', '/terms-of-service', '/about'].includes(normalizedPathname);

  // Rutas donde NO se debe mostrar el footer (pantallas de juego activo)
  // Se oculta en las salas de juego (/room/...) y en el juego de mesa activo (/table/.../play)
  const isGameScreen = normalizedPathname.startsWith('/room/') || 
                      (normalizedPathname.startsWith('/table/') && normalizedPathname.endsWith('/play'));

  // Mostrar footer solo si NO tiene su propio footer Y NO es una pantalla de juego Y NO es página 404
  const showAppFooter = !hasOwnFooter && !isGameScreen && !is404Page;

  // En Home movemos los anuncios inferiores dentro de la propia página (encima de "Juegos recomendados")
  const showBottomAds = showAppFooter && normalizedPathname !== '/';

  return (
    <>
      <div className="sticky top-0 z-50">
        <Header />
        <TopBannerAd />
      </div>
      {children}
      {showAppFooter && <Footer />}
      <CookieConsent />
    </>
  );
};

const StructuredDataManager: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    const locale = getLocaleFromPathname(location.pathname);

    const appScript = document.createElement('script');
    appScript.type = 'application/ld+json';
    appScript.text = JSON.stringify(getStructuredData(locale));
    document.head.appendChild(appScript);

    const websiteScript = document.createElement('script');
    websiteScript.type = 'application/ld+json';
    websiteScript.text = JSON.stringify(getWebSiteStructuredData(locale));
    document.head.appendChild(websiteScript);

    const navScript = document.createElement('script');
    navScript.type = 'application/ld+json';
    navScript.text = JSON.stringify(getBreadcrumbStructuredData(locale));
    document.head.appendChild(navScript);

    const orgScript = document.createElement('script');
    orgScript.type = 'application/ld+json';
    orgScript.text = JSON.stringify(getOrganizationStructuredData(locale));
    document.head.appendChild(orgScript);

    return () => {
      document.head.removeChild(appScript);
      document.head.removeChild(websiteScript);
      document.head.removeChild(navScript);
      document.head.removeChild(orgScript);
    };
  }, [location.pathname]);

  return null;
};

const App: React.FC = () => {
  return (
    <HelmetProvider>
      <Router>
        <LocaleSync>
          <StructuredDataManager />
          <AppLayout>
            <Routes>
              {(() => {
                const withPrefix = (prefix: '' | '/en' | '/pt', routePath: string) => {
                  if (routePath === '/') return prefix || '/';
                  return `${prefix}${routePath}`;
                };

                const renderRoutes = (prefix: '' | '/en' | '/pt') => (
                  <>
                    <Route path={withPrefix(prefix, '/')} element={<HomePage />} />
                    <Route path={withPrefix(prefix, '/create')} element={<CreateRoom />} />
                    <Route path={withPrefix(prefix, '/join')} element={<JoinRoom />} />
                    <Route path={withPrefix(prefix, '/room/:roomCode')} element={<GameRoom />} />
                    <Route path={withPrefix(prefix, '/table')} element={<TableSetup />} />
                    <Route path={withPrefix(prefix, '/table/:tableId/play')} element={<TableGame />} />

                    {/* Rutas del blog */}
                    <Route path={withPrefix(prefix, '/blog')} element={<BlogHome />} />
                    <Route path={withPrefix(prefix, '/blog/modalidad')} element={<BlogModalidades />} />
                    <Route path={withPrefix(prefix, '/blog/modalidad/multijugador')} element={<BlogMultijugador />} />
                    <Route path={withPrefix(prefix, '/blog/modalidad/mesa')} element={<BlogMesa />} />

                    {/* Redirecciones de rutas antiguas del blog */}
                    <Route
                      path={withPrefix(prefix, '/blog/online')}
                      element={<Navigate to={withPrefix(prefix, '/blog/modalidad/multijugador')} replace />}
                    />
                    <Route
                      path={withPrefix(prefix, '/blog/mesa')}
                      element={<Navigate to={withPrefix(prefix, '/blog/modalidad/mesa')} replace />}
                    />

                    {/* Páginas estáticas */}
                    <Route path={withPrefix(prefix, '/contact')} element={<Contact />} />
                    <Route path={withPrefix(prefix, '/privacy-policy')} element={<PrivacyPolicy />} />
                    <Route path={withPrefix(prefix, '/terms-of-service')} element={<TermsOfService />} />
                    <Route path={withPrefix(prefix, '/about')} element={<AboutUs />} />
                  </>
                );

                return (
                  <>
                    {renderRoutes('')}
                    {renderRoutes('/en')}
                    {renderRoutes('/pt')}
                  </>
                );
              })()}

              {/* Ruta catch-all para 404 - debe ser la última */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </AppLayout>
        </LocaleSync>
      </Router>
    </HelmetProvider>
  );
};

export default App;
